// frontend/src/components/EventCreateModal.js

import React, { useEffect, useState } from "react";
import { api } from "api";
import LocationAutoComplete from "./LocationAutoComplete";
import DateTimeField12h from "./DateTimeField12h";
import { X } from "phosphor-react";

export default function EventCreateModal({ groupId, open, onClose, onCreated }) {
  const [title, setTitle] = useState("");
  const [startTime, setStartTime] = useState("");   // "YYYY-MM-DDTHH:MM" local string
  const [locationText, setLocationText] = useState("");
  const [description, setDescription] = useState("");
  const [locationUrl, setLocationUrl] = useState("");
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");

  // Default a start time on open (rounded to next 5m) so "Create" enables
  useEffect(() => {
    if (!open) return;
    if (startTime) return;
    const pad = (n) => String(n).padStart(2, "0");
    const step = 5;
    const now = new Date();
    const mins = now.getMinutes();
    const next = new Date(now);
    next.setMinutes(mins + (step - (mins % step || step)));
    next.setSeconds(0);
    next.setMilliseconds(0);
    const y = next.getFullYear();
    const m = pad(next.getMonth() + 1);
    const d = pad(next.getDate());
    const h = pad(next.getHours());
    const mi = pad(next.getMinutes());
    setStartTime(`${y}-${m}-${d}T${h}:${mi}`);
  }, [open, startTime]);

  const canSave = title.trim() && startTime;
  if (!open) return null;

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!canSave) return;
    setSaving(true);
    setErr("");
    try {
      const payload = {
        title: title.trim(),
        start_time: new Date(startTime).toISOString(),
        description: description.trim() || null,
        location_text: locationText.trim() || null,
        location_url: locationUrl.trim() || null,
      };
      const { data } = await api.post(`/groups/${groupId}/events`, payload);
      onCreated?.(data);
      onClose?.();
    } catch (e2) {
      setErr(e2?.response?.data?.detail || "Failed to create event");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center" role="dialog" aria-modal="true">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      {/* allow dropdowns to escape the card if needed */}
      <div className="relative z-10 w-full max-w-lg bg-white rounded-2xl shadow-[0_8px_0px_rgba(37,99,235,0.96)] overflow-visible">
        <div className="flex items-center justify-between px-5 py-3">
          <h3 className="text-lg text-blue-700 font-semibold">Create Event</h3>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700"
            aria-label="Close"
          >
            <X size={20} weight="bold" />
          </button>
        </div>

        <form onSubmit={submit} className="px-5 pb-5 pt-0 space-y-3">
          {/* Event name */}
          <div>
            <label className="block text-sm font-medium mb-1 ml-3">Event Name</label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="w-full border rounded-2xl px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-600"
              placeholder="Cooking Class @Sur La Table"
              required
            />
          </div>

          {/* Date & time (12h picker) */}
          <div>
            <label className="block text-sm font-medium mb-1 ml-3">Date & Time</label>
            <DateTimeField12h
              value={startTime}
              onChange={setStartTime}
              minuteStep={5}
              className="w-full text-sm"
              wideDate
            />
          </div>

          {/* Location (same usage as ActivityFormFields) */}
          <div>
            <label className="block text-sm ml-3 font-medium mb-1">Location</label>
            <div className="relative z-[1000] overflow-visible">
              <LocationAutoComplete
                apiKey={process.env.REACT_APP_GOOGLE_MAPS_API_KEY}
                value={locationText}
                onChange={(val) => setLocationText(val)}
                inputClassName="py-1"
                placeholder=""
                country="us"
              />
            </div>
          </div>

          {/* Description (below Location) */}
          <div>
            <label className="block text-sm ml-3 font-medium mb-1">Description</label>
            <textarea
              rows={4}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className="w-full border rounded-2xl p-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-600"
            />
          </div>

          {/* Online link (separate line) */}
          <div>
            <label className="block text-sm font-medium ml-3 mb-1">Online Link</label>
            <input
              type="url"
              value={locationUrl}
              onChange={(e) => setLocationUrl(e.target.value)}
              placeholder="Zoom/Meet link or any URL"
              className="w-full border rounded-2xl p-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-600"
            />
          </div>

          {err && <p className="text-sm text-red-600">{err}</p>}

          <div className="flex justify-end gap-2 pt-2">
            <button type="button" onClick={onClose} className="border text-sm rounded-full px-3 py-2">Cancel</button>
            <button
              type="submit"
              disabled={!canSave || saving}
              className="bg-blue-600 text-white text-sm rounded-full px-3 py-2 disabled:opacity-50"
            >
              {saving ? "Saving…" : "Create"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
